/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Button, InputAdornment, TextField } from "@material-ui/core";
import DeleteOutlined from "@material-ui/icons/DeleteOutlined";
import AddOutlined from "@material-ui/icons/AddOutlined";
import GetAppOutlined from "@material-ui/icons/GetAppOutlined";
import Search from "@material-ui/icons/Search";
import { ExternalContactsCsvUpload } from "./";
import { useDebounce } from "../../../../hooks";
import { useExternalContacts } from "../../context/externalContacts";
import { ExternalContactsAzureSync } from "./AzureAd/ExternalContactsAzureSync";

const deleteContactsLabel = (selected, total) => {
	if (selected === total) {
		return "Delete all";
	}

	return `Delete (${selected})`;
};

const exportContactsLabel = (selected, total) => {
	if (selected === total || selected === 0) {
		return "Export all";
	}

	return `Export (${selected})`;
};

/**
 * Contains actions to manage the contacts list (add, edit, delete, import, export, search, etc.)
 */
export const ExternalContactsActionsHeader = () => {
	const {
		filterContacts,
		exportContactsToCsv,
		selectedContactsCount,
		contactsCount,
		deleteContacts,
		showDeleteConfirmation,
		selectedContactIds,
		cancelDeleteConfirmation,
		startNewContact,
	} = useExternalContacts();

	const [searchTerm, setSearchTerm] = useState("");

	const debouncedSearchTerm = useDebounce(searchTerm, 200);

	useEffect(() => {
		filterContacts(debouncedSearchTerm);
	}, [debouncedSearchTerm, filterContacts]);

	const onSearchChanged = useCallback((e) => {
		setSearchTerm(e.target.value);
	}, []);

	return (
		<div style={{ gap: 15 }} className="d-flex justify-content-between align-items-end col-12 px-0">
			<div style={{ maxWidth: 420 }} className="col-12 col-lg-6 mr-4 px-0">
				<TextField
					onFocus={(e) => e.target.select()}
					autoComplete="new-password"
					fullWidth
					value={searchTerm}
					onChange={onSearchChanged}
					label="Search by name, phone # or tag"
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search />
							</InputAdornment>
						),
					}}
				/>
			</div>
			<div className="col-12 col-lg-6 px-0 d-flex justify-content-end flex-wrap">
				{selectedContactsCount > 0 && (
					<Button
						variant="outlined"
						color="secondary"
						size="small"
						onClick={() =>
							showDeleteConfirmation(
								selectedContactsCount,
								() => deleteContacts(selectedContactIds),
								() => cancelDeleteConfirmation(),
							)
						}
						startIcon={<DeleteOutlined />}
					>
						{deleteContactsLabel(selectedContactsCount, contactsCount)}
					</Button>
				)}
				<div style={{ display: "inline-flex" }} className="ml-2">
					<ExternalContactsAzureSync />
				</div>
				<div style={{ display: "inline-flex" }} className="ml-2">
					<ExternalContactsCsvUpload />
				</div>

				<Button
					variant="outlined"
					size="small"
					className="ml-2"
					disabled={contactsCount === 0}
					onClick={() => exportContactsToCsv()}
					startIcon={<GetAppOutlined />}
				>
					{exportContactsLabel(selectedContactsCount, contactsCount)}
				</Button>
				<Button variant="outlined" size="small" className="ml-2" onClick={startNewContact} startIcon={<AddOutlined />}>
					New Contact
				</Button>
			</div>
		</div>
	);
};
